import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

// WT: id of the product page main section, learned from the first full-page fetch.
// From then on the modal asks only for that section (about a quarter of the full page).
/** @type {string | null} */
let wtMainSectionId = null;
try {
  wtMainSectionId = sessionStorage.getItem('wt-qa-main-section');
} catch (error) {}

/**
 * WT: fetches the product page (or just its main section, when known) as a document
 * @param {string} url - The product page URL
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<Document>}
 */
async function wtFetchProductDocument(url, signal) {
  if (wtMainSectionId) {
    try {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', wtMainSectionId);
      const response = await fetch(sectionUrl, { signal });
      if (response.ok) {
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (html.querySelector('[data-product-grid-content]')) return html;
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
    }
    // Product with a different template: fall back to the full page below
  }

  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
  }
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const section = html.querySelector('[data-product-grid-content]')?.closest('[id^="shopify-section-"]');
  if (section) {
    wtMainSectionId = section.id.replace('shopify-section-', '');
    try {
      sessionStorage.setItem('wt-qa-main-section', wtMainSectionId);
    } catch (error) {}
  }
  return html;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<void>>} */
  #pendingPrefetch = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // WT: start loading the product page as soon as the finger touches the button (or the
    // pointer hovers it), so the modal opens sooner after the click
    this.addEventListener('pointerdown', this.#prefetch, { signal: this.#cartUpdateAbortController.signal });
    this.addEventListener('pointerenter', this.#prefetch, { signal: this.#cartUpdateAbortController.signal });
  }

  /**
   * WT: fetches and caches the product grid ahead of the click
   */
  #prefetch = () => {
    const url = this.productPageUrl;
    if (!url || this.dataset.usesSellingPlans === 'true') return;
    if (this.#cachedContent.has(url) || this.#pendingPrefetch.has(url)) return;

    const request = wtFetchProductDocument(url)
      .then((html) => {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) this.#cachedContent.set(url, /** @type {Element} */ (gridElement.cloneNode(true)));
      })
      .catch(() => {})
      .finally(() => this.#pendingPrefetch.delete(url));
    this.#pendingPrefetch.set(url, request);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // WT: if the prefetch started on pointerdown is still running, wait for it instead of fetching twice
    const pending = this.#pendingPrefetch.get(currentUrl);
    if (pending) await pending;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      return await wtFetchProductDocument(productPageUrl, this.#abortController.signal);
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      // WT: se varianti e pulsante stanno nello stesso riquadro (scheda d'acquisto), si porta
      // tutto il riquadro: disponibilità, calcolo metri, pagamenti e barra spedizione compresi.
      // Il riquadro deve contenere varianti e pulsante ma non il titolo (cioè non l'intera colonna).
      let purchaseCard = variantPicker ? (productFormComponent?.closest('.group-block') ?? null) : null;
      while (purchaseCard && variantPicker && !purchaseCard.contains(variantPicker)) {
        purchaseCard = purchaseCard.parentElement?.closest('.group-block') ?? null;
      }

      productGrid.appendChild(productHeader);

      if (purchaseCard && !purchaseCard.querySelector('.view-product-title')) {
        purchaseCard.classList.add('wt-qa-card');
        // I colori del riquadro (sfondo, bordo) stanno in uno <style> della colonna che viene tolta
        const colorClass = [...purchaseCard.classList].find((name) => name.startsWith('color-custom-'));
        if (colorClass) {
          for (const style of productGrid.querySelectorAll('style')) {
            if (style.textContent?.includes(colorClass)) productGrid.appendChild(style.cloneNode(true));
          }
        }
        productGrid.appendChild(purchaseCard);
      } else {
        if (variantPicker) {
          productGrid.appendChild(variantPicker);
        }
        if (productFormComponent) {
          productGrid.appendChild(productFormComponent);
        }
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    // WT: define the block elements BEFORE they enter the page. On first open Safari does not
    // upgrade elements that arrived from the parsed document and were defined afterwards
    // (stock and "Totale selezionato" stayed empty until the second open).
    this.#runBlockScripts(productGrid);

    morph(modalContent, productGrid);

    this.#upgradeBlockElements(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * WT: safety net for custom elements that are defined but still not upgraded after the morph:
   * replace them with a copy created in this document, which upgrades immediately.
   * @param {Element} modalContent - The modal content element
   */
  #upgradeBlockElements(modalContent) {
    for (const element of modalContent.querySelectorAll('*')) {
      const definition = element.localName.includes('-') ? customElements.get(element.localName) : undefined;
      if (!definition) continue;

      // The WT block elements (stock, "Totale selezionato", shipping bar) set up their content in
      // connectedCallback. When the morph reuses one from the previous product, only its markup
      // changes and it stays empty: replace it with a fresh copy so it starts again.
      const isWtBlock =
        element.localName.startsWith('variant-stock-indicator-') ||
        element.localName === 'metri-calculator' ||
        element.localName === 'wt-free-shipping';

      if (element instanceof definition && !isWtBlock) continue;
      if (!isWtBlock) customElements.upgrade(element);
      if (isWtBlock || !(element instanceof definition)) element.replaceWith(document.importNode(element, true));
    }
  }

  /**
   * WT: gli script arrivati con il contenuto della finestra non partono da soli.
   * Esegue solo quelli dei blocchi marcati `data-wt-qa` (calcolo metri, disponibilità,
   * barra spedizione); ognuno controlla da sé di non registrarsi due volte.
   * @param {Element} modalContent - The modal content element
   */
  #runBlockScripts(modalContent) {
    for (const blockScript of modalContent.querySelectorAll('script[data-wt-qa]')) {
      const script = document.createElement('script');
      script.textContent = blockScript.textContent;
      document.head.appendChild(script);
      script.remove();
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
